import type { CSSProperties, ReactNode } from 'react';
import Link from 'next/link';
import './v4-sections.css';

// ─── SectLabel ──────────────────────────────────────────────────────
// Geist Mono eyebrow label with 1em horizontal rule prefix.
export function SectLabel({
  children,
  color = 'var(--dim)',
  className = '',
  id,
}: {
  children: ReactNode;
  color?: string;
  className?: string;
  id?: string;
}) {
  return (
    <div
      id={id}
      className={`v4-eyebrow ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        fontFamily: 'var(--font-geist-mono)',
        fontSize: '11px',
        letterSpacing: '0.24em',
        textTransform: 'uppercase',
        color,
        marginBottom: '18px',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '28px',
          height: '1px',
          background: 'currentColor',
          display: 'inline-block',
        }}
      />
      {children}
    </div>
  );
}

// ─── SectH2 ─────────────────────────────────────────────────────────
// Section heading. Pass <em style={{ color: 'var(--cyan-glow)' }}>word</em>
// inline for Instrument Serif italic accents.
export function SectH2({
  children,
  className = '',
  id,
  style: extraStyle = {},
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  style?: CSSProperties;
}) {
  return (
    <h2
      id={id}
      className={className}
      style={{
        fontFamily: 'var(--font-geist-sans)',
        fontSize: 'clamp(40px, 5.2vw, 80px)',
        fontWeight: 500,
        lineHeight: 1.05,
        letterSpacing: '-0.03em',
        color: 'var(--paper)',
        margin: '0 0 24px',
        maxWidth: '22ch',
        ...extraStyle,
      }}
    >
      {children}
    </h2>
  );
}

// ─── SectLede ───────────────────────────────────────────────────────
// Section intro paragraph, dim, 18px, max-width for readability.
export function SectLede({
  children,
  className = '',
  style: extraStyle = {},
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <p
      className={className}
      style={{
        fontFamily: 'var(--font-geist-sans)',
        fontSize: '18px',
        lineHeight: 1.55,
        color: 'var(--dim)',
        maxWidth: '60ch',
        margin: '0 0 40px',
        ...extraStyle,
      }}
    >
      {children}
    </p>
  );
}

// ─── GlassCard ──────────────────────────────────────────────────────
// Reusable glass-fill card container.
export function GlassCard({
  children,
  className = '',
  style: extraStyle = {},
  radius = 20,
  padding = 28,
  accent,
  hoverable = false,
  highlightLine = false,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  radius?: number;
  padding?: number | string;
  accent?: 'cyan' | 'amber' | 'jade' | 'rose';
  hoverable?: boolean;
  highlightLine?: boolean;
  as?: 'div' | 'article' | 'section';
}) {
  const classes = [
    'v4-card',
    hoverable && 'v4-card--hoverable',
    accent && `v4-card--${accent}`,
    highlightLine && 'v4-card--highlight-line',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Tag
      className={classes}
      style={{
        borderRadius: `${radius}px`,
        padding: typeof padding === 'number' ? `${padding}px` : padding,
        ...extraStyle,
      }}
    >
      {children}
    </Tag>
  );
}

// ─── V4CtaButton ────────────────────────────────────────────────────
// Primary cyan-gradient pill CTA. Renders <Link> if href provided, <button> otherwise.
export function V4CtaButton({
  href,
  onClick,
  children,
  size = 'md',
  className = '',
  type = 'button',
}: {
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  size?: 'md' | 'lg';
  className?: string;
  type?: 'button' | 'submit';
}) {
  const sizing =
    size === 'lg'
      ? { padding: '18px 34px', fontSize: '16px' }
      : { padding: '14px 26px', fontSize: '15px' };
  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'var(--font-geist-sans)',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    color: 'var(--ink)',
    background:
      'linear-gradient(135deg, var(--cyan-glow) 0%, var(--paper) 100%)',
    borderRadius: '999px',
    border: '1px solid rgba(125,250,255,0.4)',
    boxShadow: '0 10px 30px -10px rgba(34,211,238,0.55)',
    textDecoration: 'none',
    cursor: 'pointer',
    ...sizing,
  };
  if (href) {
    return (
      <Link href={href} onClick={onClick} className={`v4-cta-primary ${className}`} style={style}>
        {children}
      </Link>
    );
  }
  return (
    <button
      type={type}
      onClick={onClick}
      className={`v4-cta-primary ${className}`}
      style={style}
    >
      {children}
    </button>
  );
}

// ─── V4GhostButton ──────────────────────────────────────────────────
// Secondary ghost-pill CTA. variant selects border + text color.
export function V4GhostButton({
  href,
  onClick,
  children,
  variant = 'cyan',
  size = 'md',
  className = '',
}: {
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  variant?: 'cyan' | 'amber' | 'jade' | 'paper';
  size?: 'md' | 'lg';
  className?: string;
}) {
  const tint = {
    cyan: { color: 'var(--cyan-glow)', border: 'rgba(125,250,255,0.4)' },
    amber: { color: 'var(--amber-glow)', border: 'rgba(255,213,146,0.4)' },
    jade: { color: 'var(--jade-glow)', border: 'rgba(185,246,192,0.4)' },
    paper: { color: 'var(--paper)', border: 'var(--hair-2)' },
  }[variant];
  const sizing =
    size === 'lg'
      ? { padding: '18px 32px', fontSize: '16px' }
      : { padding: '14px 24px', fontSize: '15px' };
  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'var(--font-geist-sans)',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    color: tint.color,
    background: 'transparent',
    border: `1px solid ${tint.border}`,
    borderRadius: '999px',
    textDecoration: 'none',
    cursor: 'pointer',
    ...sizing,
  };
  if (href) {
    return (
      <Link
        href={href}
        onClick={onClick}
        className={`v4-cta-ghost v4-cta-ghost--${variant} ${className}`}
        style={style}
      >
        {children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`v4-cta-ghost v4-cta-ghost--${variant} ${className}`}
      style={style}
    >
      {children}
    </button>
  );
}
